import type {
  ButtonHTMLAttributes,
  ReactNode,
  RefObject,
  RefAttributes,
  TextareaHTMLAttributes,
} from 'react'

export type RichEditFormat = 'plain' | 'html'
export type RichEditVariant = 'std' | 'extended' | 'blocks' | 'canvas' | 'contacts'
export type RichEditPublicVariant = Exclude<RichEditVariant, 'contacts'>
export type RichEditMode = 'plain' | RichEditPublicVariant

export interface RichEditPolicy {
  enabledModes: readonly RichEditMode[]
  defaultMode: RichEditMode
}

export interface RichEditFeatures {
  customElements: boolean
  headings: boolean
  colors: boolean
  tables: boolean
  images: boolean
  maxDocumentBytes: number
}

export interface RichEditSkin {
  root: string
  modeBar: string
  modeButton: string
  modeButtonActive: string
  toolbar: string
  toolbarGroup: string
  button: string
  buttonActive: string
  select: string
  color: string
  surfaceWrap: string
  surface: string
  status: string
  blocks: {
    toolbar: string
    stage: string
    block: string
    blockSelected: string
    blockContent: string
    handle: string
    empty: string
  }
  canvas: {
    toolbar: string
    stage: string
    viewport: string
    fallback: string
  }
}

export interface RichEditChangeMeta {
  reason: string
  variant: RichEditVariant
}

export interface RichEditProps {
  value: string
  format: RichEditFormat
  variant: RichEditVariant
  onChange: (value: string, meta: RichEditChangeMeta) => void
  onFormatChange: (format: RichEditFormat, value: string) => void
  skin: RichEditSkin
  features?: Partial<RichEditFeatures>
  canonicalRef?: RefObject<HTMLTextAreaElement | null>
  syncKey?: string | number
  plainControl?: ReactNode
  toolbarAfter?: ReactNode
  modeButtonProps?: ButtonHTMLAttributes<HTMLButtonElement> & RefAttributes<HTMLButtonElement>
  plainTextareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement>
  policy?: RichEditPolicy
  active?: boolean
  sessionKey?: string | number
}

export interface RichEditVariantProps {
  value: string
  onChange: (value: string, reason: string) => void
  skin: RichEditSkin
  features: RichEditFeatures
  surfaceRef: RefObject<HTMLElement | null>
}

export const DEFAULT_RICH_EDIT_FEATURES: RichEditFeatures = {
  customElements: true,
  headings: true,
  colors: true,
  tables: true,
  images: true,
  maxDocumentBytes: 1_500_000,
}
